import axios from "axios";
import Swal from "sweetalert2";

let rawBaseUrl = process.env.REACT_APP_API_BASE_URL ? process.env.REACT_APP_API_BASE_URL.trim() : "";

// When developing on localhost:3000, if baseURL is empty or relative '/api', route directly to backend API port 7199
if (
  !rawBaseUrl ||
  (typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") &&
    rawBaseUrl === "/api")
) {
  rawBaseUrl = "https://localhost:7199/api";
}

const API_BASE_URL = rawBaseUrl;

// Create axios instance with 15s timeout to avoid premature network aborts
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Automatically add token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Global Response Handler for Status & Errors
api.interceptors.response.use(
  (response) => {
    const data = response.data;
    const status = data.status !== undefined ? data.status : data.Status;
    const message = data.message || data.Message || data.msg || data.Msg;
    const method = response.config?.method?.toLowerCase();
    const isMutation = method === "post" || method === "put" || method === "delete";

    // Toast configuration
    const Toast = (icon, title, text, timer = 3000) => {
      Swal.fire({
        icon,
        title,
        text,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: timer,
        timerProgressBar: !!timer,
        showCloseButton: !timer,
      });
    };

    // Show success toast on mutations (add/update/delete)
    if (isMutation && (status === true || status === "true")) {
      Toast("success", "Success", message || "Operation successful!");
    }
    // If API explicitly returns status: false on mutation
    else if (isMutation && (status === false || status === "false")) {
      Toast("error", "Operation Failed", message || "Something went wrong!", null);
    }

    return response;
  },
  (error) => {
    // If request marked as silent (e.g. background sync or offline check), do not pop up intrusive alerts
    if (error.config?.silent) {
      return Promise.reject(error);
    }

    const data = error.response?.data || {};
    const message =
      data.message ||
      data.Message ||
      data.msg ||
      data.Msg ||
      (typeof data === "string" ? data : error.message);

    // Handle 401 errors (token expired)
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");
      window.location.href = "/login";
      return Promise.reject(error);
    }

    // Only show error alert if user is actively interacting (not a silent offline failure)
    if (navigator.onLine && error.response?.status !== 0) {
      Swal.fire({
        icon: "error",
        title: "API Error",
        text: message || "Unable to connect to server",
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        showCloseButton: true,
        timer: 4000,
      });
    }

    return Promise.reject(error);
  }
);

export default api;
